import { CaretDown, Check } from "@phosphor-icons/react";
import { networkLabel } from "@/lib/networkLabels";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Dropdown order; "all" is the sentinel, network labels come from
// @/lib/networkLabels (single source). Admin lists one option per RGA account
// (rga_vishy / rga_ashok) — no combined "rga" here, because in a multi-select
// it would overlap both accounts and make include / exclude contradictory.
const FEED_PARTNER_KEYS = [
  "impact", "skimlinks", "mrge", "partnerize", "yieldkit", "shopnomix", "cj",
  "admedia", "pubadhub", "pubadhub_kevin", "flexoffers", "shopnomix_kevin",
  "siteplug", "giddyup", "pronomix", "maxbounty", "cashbackhive", "fatcoupon",
  "rga_vishy", "rga_ashok",
];
export const FEED_PARTNER_OPTIONS = [
  { value: "all", label: "All feed partners" },
  ...FEED_PARTNER_KEYS.map((k) => ({ value: k, label: networkLabel(k) })),
];

const RGA_ACCOUNT_VALUES = new Set(["rga_vishy", "rga_ashok"]);

// Publishers never see the RGA account split: one "rga" option, which the
// backend expands to both accounts (services/click_log_query.py).
export const PUBLISHER_FEED_PARTNER_OPTIONS = [
  ...FEED_PARTNER_OPTIONS.filter((o) => !RGA_ACCOUNT_VALUES.has(o.value)),
  { value: "rga", label: "LD" },
];

const PARTNER_OPTIONS = FEED_PARTNER_OPTIONS.filter((o) => o.value !== "all");
const PUBLISHER_PARTNER_OPTIONS = PUBLISHER_FEED_PARTNER_OPTIONS.filter((o) => o.value !== "all");

export function parseNetworkFilter(value) {
  if (!value || value === "all") return [];
  if (Array.isArray(value)) {
    return [...new Set(value.map((v) => String(v || "").trim().toLowerCase()).filter((v) => v && v !== "all"))];
  }
  return [...new Set(
    String(value)
      .split(",")
      .map((v) => v.trim().toLowerCase())
      .filter((v) => v && v !== "all"),
  )];
}

export function serializeNetworkFilter(selected) {
  const uniq = parseNetworkFilter(Array.isArray(selected) ? selected.join(",") : selected);
  return uniq.length ? uniq.join(",") : "all";
}

export function appendNetworkParam(params, network, discloseNetworks = true) {
  if (!discloseNetworks) return;
  const parsed = parseNetworkFilter(network);
  if (parsed.length) params.set("network", parsed.join(","));
}

export function appendRouteFromParam(params, routeFrom) {
  const parsed = parseNetworkFilter(routeFrom);
  if (parsed.length) params.set("route_from", parsed.join(","));
}

function mergeOptions(extraOptions = [], base = PARTNER_OPTIONS) {
  const seen = new Set(base.map((o) => o.value));
  const extras = [];
  for (const raw of extraOptions) {
    const value = String(raw?.value || raw || "").trim().toLowerCase();
    if (!value || value === "all" || seen.has(value)) continue;
    seen.add(value);
    extras.push({ value, label: raw?.label || value });
  }
  return [...base, ...extras];
}

export function triggerLabel(selected, options, allLabel, summarizeExcept = false) {
  if (!selected.length) return allLabel;
  const missing = options.filter((o) => !selected.includes(o.value));
  if (summarizeExcept && missing.length === 1 && selected.length > 1) return `All except ${missing[0].label}`;
  return selected
    .map((v) => options.find((o) => o.value === v)?.label || v)
    .join(", ");
}

function VisibleCheckbox({ checked }) {
  return (
    <span
      aria-hidden="true"
      className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border ${
        checked
          ? "border-[#002FA7] bg-[#002FA7] text-white"
          : "border-slate-400 bg-white"
      }`}
    >
      {checked ? <Check size={11} weight="bold" /> : null}
    </span>
  );
}

export default function FeedPartnerSelect({
  value,
  onChange,
  testId = "filter-network",
  extraOptions = [],
  allLabel = "All feed partners",
  menuLabel = "Feed partners",
  summarizeExcept = false,
  className = "min-w-[200px] max-w-[360px]",
  // "publisher": the publisher-facing list (RGA as one "rga" option).
  audience = "admin",
}) {
  const options = mergeOptions(
    extraOptions, audience === "publisher" ? PUBLISHER_PARTNER_OPTIONS : PARTNER_OPTIONS);
  const selected = parseNetworkFilter(value);
  const allChecked = selected.length === 0;

  const emit = (next) => {
    const uniq = [...new Set(next)];
    // Checking every partner is the same as no filter.
    if (uniq.length === 0 || uniq.length >= options.length) {
      onChange?.("all");
      return;
    }
    onChange?.(uniq.join(","));
  };

  const toggle = (networkValue) => {
    if (selected.includes(networkValue)) {
      emit(selected.filter((v) => v !== networkValue));
    } else {
      emit([...selected, networkValue]);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`h-9 justify-between font-normal ${className}`}
          data-testid={testId}
          title={triggerLabel(selected, options, allLabel, summarizeExcept)}
        >
          <span className="min-w-0 truncate">{triggerLabel(selected, options, allLabel, summarizeExcept)}</span>
          <CaretDown size={12} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto" data-testid={`${testId}-menu`}>
        <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={(e) => {
            e.preventDefault();
            onChange?.("all");
          }}
          data-testid={`${testId}-all`}
        >
          <VisibleCheckbox checked={allChecked} />
          {allLabel}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {options.map((opt) => {
          const checked = selected.includes(opt.value);
          return (
            <DropdownMenuItem
              key={opt.value}
              className="cursor-pointer"
              onSelect={(e) => {
                e.preventDefault();
                toggle(opt.value);
              }}
              data-testid={`${testId}-${opt.value}`}
            >
              <VisibleCheckbox checked={checked} />
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
